'use client';

/**
 * AI Message Bubble — renders a single chat message with markdown.
 *
 * User messages: right-aligned, primary background.
 * Assistant messages: left-aligned, muted background, markdown rendered.
 *
 * Uses react-markdown + remark-gfm for:
 *   - Bold, italic, strikethrough
 *   - Headings (h1-h4)
 *   - Lists (ordered + unordered)
 *   - Links (target=_blank + rel=noopener)
 *   - Tables (GFM)
 *   - Code blocks (inline + block)
 *
 * Spec ref: docs/SPEC-174-1-PROFILEIA-AI-HUB.md §6.2.
 *
 * Anti-P-042: 0 `console.*` statements.
 */

import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { User, Bot } from 'lucide-react';
import type { AIChatMessage } from '@profileia/shared';

interface AIMessageBubbleProps {
  message: AIChatMessage;
  /** Whether this is the local user's message (right-aligned). */
  isUser?: boolean;
  /** Optional assistant name for the avatar tooltip. */
  assistantName?: string;
}

function AIMessageBubbleImpl({
  message,
  isUser = false,
  assistantName,
}: AIMessageBubbleProps) {
  const Icon = isUser ? User : Bot;

  return (
    <div
      className={cn(
        'flex gap-2.5 px-4 py-3',
        isUser ? 'flex-row-reverse' : 'flex-row',
      )}
      role="article"
      aria-label={isUser ? 'Tu mensaje' : `Respuesta de ${assistantName ?? 'asistente'}`}
    >
      {/* Avatar */}
      <div
        className={cn(
          'shrink-0 size-7 rounded-full flex items-center justify-center',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground',
        )}
        aria-hidden="true"
      >
        <Icon className="size-4" />
      </div>

      {/* Message body — Sprint 90 fix: explicit colors for dark mode contrast */}
      <div
        className={cn(
          'flex-1 min-w-0 rounded-lg px-3.5 py-2.5 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-secondary text-secondary-foreground border border-border',
        )}
      >
        {isUser ? (
          // User messages: plain text (no markdown — they didn't write markdown).
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        ) : (
          // Assistant messages: render markdown.
          <div className="ai-markdown break-words">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Links open in new tab + security.
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                  >
                    {children}
                  </a>
                ),
                // Tables: horizontal scroll on mobile.
                table: ({ children }) => (
                  <div className="my-2 overflow-x-auto">
                    <table className="min-w-full text-xs border-collapse">
                      {children}
                    </table>
                  </div>
                ),
                th: ({ children }) => (
                  <th className="border border-border px-2 py-1 text-left font-semibold bg-muted">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border border-border px-2 py-1">{children}</td>
                ),
                // Code blocks: monospace + background.
                code: ({ className, children }) => {
                  const isInline = !className?.includes('language-');
                  return isInline ? (
                    <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">
                      {children}
                    </code>
                  ) : (
                    <code className={className}>{children}</code>
                  );
                },
                pre: ({ children }) => (
                  <pre className="my-2 p-3 rounded-lg bg-muted overflow-x-auto text-xs">
                    {children}
                  </pre>
                ),
                // Headings: smaller in chat context.
                h1: ({ children }) => <h3 className="text-base font-bold mt-3 mb-1">{children}</h3>,
                h2: ({ children }) => <h4 className="text-sm font-bold mt-2 mb-1">{children}</h4>,
                h3: ({ children }) => <h5 className="text-sm font-semibold mt-2 mb-1">{children}</h5>,
                // Lists: tighter spacing.
                ul: ({ children }) => <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>,
                p: ({ children }) => <p className="my-1 leading-relaxed">{children}</p>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

export const AIMessageBubble = memo(AIMessageBubbleImpl);
